<template>
  <div>
    <eden-table-actions :title="title" @search="searchBeautician">
      <template slot="actions">
        <beautician-export :loading="loading" :data="pageData" />
      </template>
    </eden-table-actions>
    <eden-loader v-if="loading" />
    <template v-else>
      <template v-if="pageData.length">
        <el-table :data="pageData">
          <el-table-column width="220">
            <template slot="header">
              <div class="table--header">
                <span>Name</span>
              </div>
            </template>
            <template v-slot="scope">
              <router-link
                :to="{
                  name: 'logistics.directory.beautician',
                  params: { id: scope.row.id },
                }"
              >
                <span class="font-sm text-primary">
                  {{ formatFullName(scope.row) }}</span
                ></router-link
              >
            </template>
          </el-table-column>
          <el-table-column width="100">
            <template slot="header">
              <div class="table--header">
                <span>Status</span>
              </div>
            </template>
            <template v-slot="scope">
              <el-tag :type="setType(scope.row.status)">
                {{ formatText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column width="150">
            <template slot="header">
              <div class="table--header">
                <span>Phone Number</span>
              </div>
            </template>
            <template v-slot="scope">
              <span class="font-sm">
                {{ formatPhoneNumber(scope.row.phone_number) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column>
            <template slot="header">
              <div class="table--header">
                <span>Location area they live</span>
              </div>
            </template>
            <template slot-scope="scope">
              <span class="font-sm">
                {{ scope.row.location_area && scope.row.location_area.name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column>
            <template slot="header">
              <div class="table--header">
                <span>Categories</span>
              </div>
            </template>
            <template v-slot="scope">
              <span class="font-sm">
                {{
                  formatArrayToString({
                    data: scope.row.categories,
                    property: "name",
                  })
                }}
              </span>
            </template>
          </el-table-column>
          <el-table-column width="200">
            <template slot="header">
              <div class="table--header">
                <span>Preferred Days</span>
              </div>
            </template>
            <template v-slot="scope">
              <span
                class="is-flex flex-wrap"
                v-if="scope.row.preferred_working_days.length"
                >{{ setWorkingDays(scope.row.preferred_working_days) }}</span
              >
            </template>
          </el-table-column>
          <el-table-column
            v-if="allowAccessFor(['admin', 'operations'])"
            width="80"
          >
            <template v-slot="scope">
              <beautician-actions
                :beautician.sync="scope.row"
                @update="getBeauticians"
              />
            </template>
          </el-table-column>
        </el-table>
        <eden-pagination
          v-if="showPagination"
          :from="from"
          :to="to"
          :total="total"
          :current-page.sync="page"
        />
      </template>
      <eden-content-empty v-else :text="'No data'" />
    </template>
  </div>
</template>

<script>
import beauticians from "@/requests/logistics/directory/beauticians";
import * as actions from "@/store/action-types";

export default {
  name: "Beauticians",
  components: {
    BeauticianExport: () => import("./BeauticianExport"),
    BeauticianActions: () => import("./Beautician/Actions/BeauticianActions"),
  },
  props: {
    totalBeauticians: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: false,
      page: 1,
      pageData: [],
      showPagination: true,
    };
  },

  computed: {
    beauticians() {
      return this.$store.getters.directory.beauticians;
    },
    from() {
      return this.beauticians.pages[this.page].from;
    },
    to() {
      return this.beauticians.pages[this.page].to;
    },
    total() {
      return this.beauticians.total;
    },
    title() {
      const total = this.total;
      const result = this.pageData.length;
      return `${this.showPagination ? total || 0 : result} Beauticians`;
    },
    setTotalBeauticians: {
      get() {
        return this.totalBeauticians;
      },
      set(value) {
        this.$emit("update:totalBeauticians", value);
      },
    },
  },

  watch: {
    page() {
      const pageFetched = !!this.beauticians.pages[this.page];
      if (pageFetched) {
        this.setPageData();
      } else {
        this.getBeauticians();
      }
    },
  },
  created() {
    this.getBeauticians();
  },

  methods: {
    getBeauticians() {
      this.loading = true;
      this.$store
        .dispatch(actions.GET_LOGISTICS_DIRECTORY, {
          directory: "beauticians",
          page: this.page,
        })
        .then(() => {
          this.setPageData();
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          const errorMessage = error.response.data;
          if (errorMessage.errors) {
            const errorKeys = Object.keys(errorMessage.errors);
            this.$message.error(errorMessage.errors[errorKeys[0]].join(""));
          } else {
            this.$message.error(errorMessage.message);
          }
        });
    },
    searchBeautician(query) {
      if (query === "") {
        this.setPageData();
        return;
      }

      this.loading = true;
      beauticians
        .search(query)
        .then((response) => {
          const { status, data } = response;
          if (status) {
            this.pageData = data.data;
            this.showPagination = false;
          }
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          const errorMessage = error.response.data;
          if (errorMessage.errors) {
            const errorKeys = Object.keys(errorMessage.errors);
            this.$message.error(errorMessage.errors[errorKeys[0]].join(""));
          } else {
            this.$message.error(errorMessage.message);
          }
        });
    },
    setPageData() {
      this.pageData = this.beauticians.pages[this.page].data;
      this.setTotalBeauticians = this.total ?? 0;
      this.showPagination = true;
    },
    setWorkingDays(days) {
      return this.formatToTitleCase(
        this.sortWeekDays(days, "short").join(", "),
      );
    },
  },
};
</script>
